import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Plus, Edit, Trash2, Copy, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import { projectAPI } from '../utils/api';

const COMMON_ROLES = [
  'Florist', 'Makeup Artist', 'Hair Stylist', 'Caterer', 'DJ',
  'Band', 'Decorator', 'Mandap', 'Venue', 'Cake', 'Transport',
  'Photographer', 'Videographer', 'Planner', 'Other'
];

const cleanHandle = (h) => (h || '').trim().replace(/^@/, '');

const copyText = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  } catch {
    toast.error('Copy failed — try long-press to copy manually');
  }
};

const VendorsSection = ({ project, onProjectUpdated }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', role: '', instagram: '' });
  const [saving, setSaving] = useState(false);

  const vendors = project.vendors || [];
  const brideHandle = cleanHandle(project.bride_instagram);
  const groomHandle = cleanHandle(project.groom_instagram);

  const allHandlesString = () => {
    const parts = [];
    if (brideHandle) parts.push(`@${brideHandle}`);
    if (groomHandle) parts.push(`@${groomHandle}`);
    vendors.forEach(v => { if (v.instagram) parts.push(`@${cleanHandle(v.instagram)}`); });
    return parts.join(' ');
  };

  const allNamesAndHandles = () => {
    const parts = [];
    if (project.bride_name && brideHandle) parts.push(`${project.bride_name} (@${brideHandle})`);
    if (project.groom_name && groomHandle) parts.push(`${project.groom_name} (@${groomHandle})`);
    vendors.forEach(v => {
      if (v.instagram) parts.push(`${v.name} (@${cleanHandle(v.instagram)})`);
    });
    return parts.join(', ');
  };

  const persistVendors = async (nextVendors) => {
    setSaving(true);
    try {
      const res = await projectAPI.update(project.project_id, { vendors: nextVendors });
      onProjectUpdated(res.data);
    } catch (err) {
      toast.error('Failed to save vendors');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setForm({ name: '', role: '', instagram: '' });
    setAdding(false);
    setEditingId(null);
  };

  const handleSubmit = async () => {
    const name = form.name.trim();
    if (!name) {
      toast.error('Vendor name required');
      return;
    }
    const payload = {
      name,
      role: form.role.trim() || null,
      instagram: cleanHandle(form.instagram) || null,
    };
    let next;
    if (editingId) {
      next = vendors.map(v => v.vendor_id === editingId ? { ...v, ...payload } : v);
    } else {
      next = [...vendors, payload];
    }
    await persistVendors(next);
    resetForm();
    toast.success(editingId ? 'Vendor updated' : 'Vendor added');
  };

  const handleEdit = (v) => {
    setEditingId(v.vendor_id);
    setAdding(true);
    setForm({ name: v.name || '', role: v.role || '', instagram: cleanHandle(v.instagram) });
  };

  const handleDelete = async (vendor_id) => {
    if (!window.confirm('Delete this vendor?')) return;
    const next = vendors.filter(v => v.vendor_id !== vendor_id);
    await persistVendors(next);
    toast.success('Vendor removed');
  };

  const totalCount = vendors.length + (brideHandle ? 1 : 0) + (groomHandle ? 1 : 0);

  return (
    <div className="card p-4 sm:p-6" data-testid="vendors-section">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button
          onClick={() => setCollapsed(c => !c)}
          className="flex items-center gap-2 hover:text-gold transition-colors"
          data-testid="vendors-toggle"
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
          <h2 className="text-xl sm:text-2xl font-serif">Vendors & Handles ({totalCount})</h2>
        </button>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => copyText(allHandlesString(), 'All handles')}
            disabled={totalCount === 0}
            className="px-3 py-1.5 text-xs border border-black/15 text-foreground hover:bg-black hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
            data-testid="copy-all-handles"
            title="Copy all @handles as one string (perfect for Instagram caption)"
          >
            <Copy size={12} /> COPY ALL HANDLES
          </button>
          <button
            onClick={() => copyText(allNamesAndHandles(), 'Full credits')}
            disabled={totalCount === 0}
            className="px-3 py-1.5 text-xs border border-border hover:border-gold transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
            data-testid="copy-all-credits"
            title="Copy names + handles (for a 'Credits' caption)"
          >
            <Copy size={12} /> COPY CREDITS
          </button>
          <button
            onClick={() => { resetForm(); setAdding(true); }}
            className="px-3 py-1.5 text-xs bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-1.5"
            data-testid="add-vendor-btn"
          >
            <Plus size={12} /> ADD VENDOR
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="mt-4 space-y-3" data-testid="vendors-list">
          {/* Bride & Groom handles */}
          {(brideHandle || groomHandle) && (
            <div className="border border-gold/30 bg-gold/5 p-3">
              <p className="text-[10px] uppercase tracking-wider text-gold font-medium mb-2">The Couple</p>
              <div className="space-y-1.5">
                {project.bride_name && brideHandle && (
                  <VendorRow
                    name={project.bride_name}
                    role="Bride"
                    handle={brideHandle}
                    onCopy={() => copyText(`@${brideHandle}`, `@${brideHandle}`)}
                    testId={`couple-bride`}
                  />
                )}
                {project.groom_name && groomHandle && (
                  <VendorRow
                    name={project.groom_name}
                    role="Groom"
                    handle={groomHandle}
                    onCopy={() => copyText(`@${groomHandle}`, `@${groomHandle}`)}
                    testId={`couple-groom`}
                  />
                )}
              </div>
              {(!brideHandle || !groomHandle) && (
                <p className="text-[11px] text-muted-foreground mt-2">
                  Tip: edit the project to add {!brideHandle ? 'bride' : ''}{(!brideHandle && !groomHandle) ? ' & ' : ''}{!groomHandle ? 'groom' : ''} Instagram handle{!brideHandle && !groomHandle ? 's' : ''}.
                </p>
              )}
            </div>
          )}

          {/* Add / Edit form */}
          {adding && (
            <div className="border border-dashed border-gold p-3 space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <input
                  type="text"
                  placeholder="Name *"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  className="px-3 py-2 text-sm border border-input bg-background"
                  autoFocus
                  data-testid="vendor-form-name"
                />
                <select
                  value={form.role}
                  onChange={e => setForm({ ...form, role: e.target.value })}
                  className="px-3 py-2 text-sm border border-input bg-background"
                  data-testid="vendor-form-role"
                >
                  <option value="">Role (optional)</option>
                  {COMMON_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <input
                  type="text"
                  placeholder="@instagram_handle"
                  value={form.instagram}
                  onChange={e => setForm({ ...form, instagram: e.target.value })}
                  className="px-3 py-2 text-sm border border-input bg-background"
                  data-testid="vendor-form-instagram"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSubmit}
                  disabled={saving}
                  className="px-3 py-1.5 text-xs bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1.5"
                  data-testid="vendor-form-save"
                >
                  <Check size={12} /> {editingId ? 'SAVE' : 'ADD'}
                </button>
                <button
                  onClick={resetForm}
                  className="px-3 py-1.5 text-xs border border-border hover:bg-accent flex items-center gap-1.5"
                  data-testid="vendor-form-cancel"
                >
                  <X size={12} /> CANCEL
                </button>
              </div>
            </div>
          )}

          {/* Vendor list */}
          {vendors.length > 0 ? (
            <div className="divide-y divide-border border border-border">
              {vendors.map((v) => (
                <div
                  key={v.vendor_id}
                  className="flex items-center justify-between gap-3 p-3 hover:bg-accent/30"
                  data-testid={`vendor-${v.vendor_id}`}
                >
                  <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm truncate">{v.name}</span>
                    {v.role && (
                      <span className="text-[10px] px-2 py-0.5 bg-muted text-muted-foreground uppercase tracking-wide">
                        {v.role}
                      </span>
                    )}
                    {v.instagram && (
                      <a
                        href={`https://instagram.com/${cleanHandle(v.instagram)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-gold hover:underline"
                      >
                        @{cleanHandle(v.instagram)}
                      </a>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {v.instagram && (
                      <button
                        onClick={() => copyText(`@${cleanHandle(v.instagram)}`, `@${cleanHandle(v.instagram)}`)}
                        className="p-1.5 border border-border hover:border-gold transition-colors"
                        title="Copy @handle"
                        data-testid={`copy-vendor-${v.vendor_id}`}
                      >
                        <Copy size={12} />
                      </button>
                    )}
                    <button
                      onClick={() => handleEdit(v)}
                      className="p-1.5 border border-border hover:border-gold transition-colors"
                      title="Edit"
                      data-testid={`edit-vendor-${v.vendor_id}`}
                    >
                      <Edit size={12} />
                    </button>
                    <button
                      onClick={() => handleDelete(v.vendor_id)}
                      className="p-1.5 border border-border hover:border-red-500 hover:text-red-500 transition-colors"
                      title="Delete"
                      data-testid={`delete-vendor-${v.vendor_id}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            !adding && (
              <p className="text-sm text-muted-foreground text-center py-4" data-testid="vendors-empty">
                No vendors added yet. Click <strong>+ ADD VENDOR</strong> to get started.
              </p>
            )
          )}
        </div>
      )}
    </div>
  );
};

const VendorRow = ({ name, role, handle, onCopy, testId }) => (
  <div className="flex items-center justify-between gap-2" data-testid={testId}>
    <div className="flex items-center gap-2 flex-wrap min-w-0">
      <span className="font-medium text-sm truncate">{name}</span>
      <span className="text-[10px] px-2 py-0.5 bg-gold/20 text-gold uppercase tracking-wide">{role}</span>
      <a
        href={`https://instagram.com/${handle}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs text-gold hover:underline"
      >
        @{handle}
      </a>
    </div>
    <button
      onClick={onCopy}
      className="p-1.5 border border-border hover:border-gold transition-colors flex-shrink-0"
      title="Copy @handle"
    >
      <Copy size={12} />
    </button>
  </div>
);

export default VendorsSection;
